import {
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  Check,
  Lock,
  Search,
  Users,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { useOrganizationCanManage } from "@/hooks/organizations/use-organization-can-manage";
import { cn } from "@/infrastructure/lib/utils";
import { useTRPC } from "@/infrastructure/trpc/react";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Skeleton } from "@/shared/ui/skeleton";
export const Route = createFileRoute(
  "/app/organization/$id/settings/github/assignees"
)({
  loader: async ({ context, params }) => {
    // Check installation first to avoid NOT_FOUND errors for orgs without GitHub App
    const installation = await context.queryClient.query(
      context.trpc.githubApp.getInstallation.queryOptions({
        organizationId: params.id,
      })
    );

    // Only prefetch GitHub data if installation exists
    if (installation) {
      await Promise.all([
        context.queryClient
          .query(
            context.trpc.githubApp.listRepositories.queryOptions(
              {
                organizationId: params.id,
              }
            )
          )
          .catch(() => undefined),
        context.queryClient
          .query(
            context.trpc.githubApp.listOrgMembers.queryOptions(
              {
                organizationId: params.id,
              }
            )
          )
          .catch(() => undefined),
        context.queryClient
          .query(
            context.trpc.githubApp.getRepoAssignees.queryOptions(
              {
                organizationId: params.id,
              }
            )
          )
          .catch(() => undefined),
      ]);
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const trpc = useTRPC();
  const [selectedRepoFullName, setSelectedRepoFullName] =
    useState<string | null>(null);
  const [memberSearch, setMemberSearch] = useState("");

  const { canManage } = useOrganizationCanManage(id);

  const { data: installation } = useQuery(
    trpc.githubApp.getInstallation.queryOptions({
      organizationId: id,
    })
  );

  const { data: repositories, isLoading: isLoadingRepos } =
    useQuery({
      ...trpc.githubApp.listRepositories.queryOptions({
        organizationId: id,
      }),
      enabled: !!installation,
    });

  const { data: orgMembers, isLoading: isLoadingMembers } =
    useQuery({
      ...trpc.githubApp.listOrgMembers.queryOptions({
        organizationId: id,
      }),
      enabled: canManage && !!installation,
    });

  const { data: repoAssignees, refetch: refetchAssignees } =
    useQuery({
      ...trpc.githubApp.getRepoAssignees.queryOptions({
        organizationId: id,
      }),
      enabled: canManage && !!installation,
    });

  const updateAssignees = useMutation(
    trpc.githubApp.updateRepoAssignees.mutationOptions({
      onSuccess: async () => {
        toast.success("Assignees updated");
        await refetchAssignees();
      },
      onError: (error) => {
        toast.error(
          error.message || "Failed to update assignees"
        );
      },
    })
  );

  const getRepoAssignees = (repoFullName: string) => {
    const found = repoAssignees?.find(
      (a) => a.repositoryFullName === repoFullName
    );
    return found?.assignees ?? [];
  };

  const handleToggleAssignee = (memberLogin: string) => {
    if (!selectedRepoFullName) return;

    const currentAssignees = getRepoAssignees(
      selectedRepoFullName
    );
    const newAssignees = currentAssignees.includes(
      memberLogin
    )
      ? currentAssignees.filter((a) => a !== memberLogin)
      : [...currentAssignees, memberLogin];

    updateAssignees.mutate({
      organizationId: id,
      repositoryFullName: selectedRepoFullName,
      assignees: newAssignees,
    });
  };

  const sortedRepos = [...(repositories ?? [])].sort(
    (a, b) => {
      const aHasAssignees =
        getRepoAssignees(a.fullName).length > 0;
      const bHasAssignees =
        getRepoAssignees(b.fullName).length > 0;

      if (aHasAssignees && !bHasAssignees) return -1;
      if (!aHasAssignees && bHasAssignees) return 1;

      return a.fullName.localeCompare(b.fullName);
    }
  );

  const selectedRepoAssignees = selectedRepoFullName
    ? getRepoAssignees(selectedRepoFullName)
    : [];

  const filteredMembers = (
    orgMembers?.filter((m) =>
      m.login
        .toLowerCase()
        .includes(memberSearch.toLowerCase())
    ) ?? []
  ).sort((a, b) => {
    const aAssigned = selectedRepoAssignees.includes(
      a.login
    );
    const bAssigned = selectedRepoAssignees.includes(
      b.login
    );

    if (aAssigned && !bAssigned) return -1;
    if (!aAssigned && bAssigned) return 1;

    return a.login.localeCompare(b.login);
  });

  const isLoading = isLoadingRepos || isLoadingMembers;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link
          to="/app/organization/$id/settings/github/issue-creation"
          params={{ id }}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Issue Creation
        </Link>
      </div>

      <div>
        <h3 className="mb-1 text-lg font-semibold">
          Repository Assignees
        </h3>
        <p className="text-sm text-muted-foreground">
          Configure default assignees for automatic issue
          creation. Select a repository to manage its
          assignees.
        </p>
      </div>

      {!installation ? (
        <Card>
          <CardContent
            variant="py12"
            className="flex flex-col items-center justify-center"
          >
            <Users className="mb-4 h-12 w-12 text-muted-foreground/30" />
            <h4 className="mb-2 text-base font-semibold">
              GitHub App Not Installed
            </h4>
            <p className="mb-4 max-w-md text-center text-sm text-muted-foreground">
              Install the GitHub App to configure repository
              assignees for automatic issue creation.
            </p>
            <Button
              render={
                <Link
                  to="/app/organization/$id/settings/github"
                  params={{ id }}
                />
              }
            >
              Go to GitHub Settings
            </Button>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-32" />
            </CardHeader>
            <CardContent variant="space2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-32" />
            </CardHeader>
            <CardContent variant="space2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Repositories List */}
          <Card>
            <CardHeader variant="compact">
              <CardTitle variant="base">
                Repositories
              </CardTitle>
              <CardDescription>
                {sortedRepos.length} repositories accessible
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sortedRepos.length > 0 ? (
                <div className="max-h-125 divide-y overflow-hidden overflow-y-auto rounded-lg border">
                  {sortedRepos.map((repo) => {
                    const assignees = getRepoAssignees(
                      repo.fullName
                    );
                    const isSelected =
                      selectedRepoFullName ===
                      repo.fullName;
                    return (
                      <button
                        type="button"
                        key={repo.id}
                        className={cn(
                          "flex w-full items-center justify-between px-4 py-3 text-left transition-colors",
                          isSelected
                            ? "bg-primary/10"
                            : "hover:bg-muted/40"
                        )}
                        onClick={() => {
                          setSelectedRepoFullName(
                            repo.fullName
                          );
                          setMemberSearch("");
                        }}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium">
                              {repo.fullName}
                            </span>
                            {repo.private && (
                              <Lock className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                            )}
                          </div>
                          {assignees.length > 0 && (
                            <p className="mt-0.5 truncate text-xs text-muted-foreground">
                              {assignees.join(", ")}
                            </p>
                          )}
                        </div>
                        {assignees.length > 0 && (
                          <Badge
                            size="number"
                            variant="secondary"
                            className="ml-2 flex-shrink-0"
                          >
                            {assignees.length}
                          </Badge>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No repositories accessible. Configure
                  repository access in GitHub App settings.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Assignees Panel */}
          <Card>
            <CardHeader variant="compact">
              <CardTitle
                variant="withIconBase"
                className="flex items-center"
              >
                <Users className="h-4 w-4" />
                Assignees
              </CardTitle>
              <CardDescription>
                {selectedRepoFullName ? (
                  <span className="font-mono text-xs">
                    {selectedRepoFullName}
                  </span>
                ) : (
                  "Select a repository to manage assignees"
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedRepoFullName ? (
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      variant="search"
                      placeholder="Search members..."
                      value={memberSearch}
                      onChange={(e) =>
                        setMemberSearch(e.target.value)
                      }
                    />
                  </div>

                  <div className="max-h-87.5 divide-y overflow-hidden overflow-y-auto rounded-lg border">
                    {filteredMembers.length > 0 ? (
                      filteredMembers.map((member) => {
                        const isSelected =
                          selectedRepoAssignees.includes(
                            member.login
                          );
                        return (
                          <button
                            type="button"
                            key={member.login}
                            className={cn(
                              "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors",
                              isSelected
                                ? "bg-primary/10"
                                : "hover:bg-muted/40"
                            )}
                            onClick={() =>
                              handleToggleAssignee(
                                member.login
                              )
                            }
                            disabled={!canManage}
                          >
                            <div
                              className={cn(
                                "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border transition-colors",
                                isSelected
                                  ? "border-primary bg-primary text-primary-foreground"
                                  : "border-input"
                              )}
                            >
                              {isSelected && (
                                <Check className="h-3 w-3" />
                              )}
                            </div>
                            <img
                              src={
                                member.avatarUrl ||
                                undefined
                              }
                              alt=""
                              className="h-8 w-8 flex-shrink-0 rounded-full"
                            />
                            <span className="text-sm font-medium">
                              {member.login}
                            </span>
                          </button>
                        );
                      })
                    ) : orgMembers &&
                      orgMembers.length > 0 ? (
                      <p className="py-8 text-center text-sm text-muted-foreground">
                        No members match your search
                      </p>
                    ) : (
                      <p className="py-8 text-center text-sm text-muted-foreground">
                        No organization members available
                      </p>
                    )}
                  </div>

                  {selectedRepoAssignees.length > 0 && (
                    <div className="border-t pt-2">
                      <p className="mb-2 text-xs text-muted-foreground">
                        Selected (
                        {selectedRepoAssignees.length})
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedRepoAssignees.map(
                          (login) => (
                            <Badge
                              key={login}
                              variant="secondary"
                            >
                              {login}
                            </Badge>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Users className="mb-4 h-12 w-12 text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">
                    Select a repository from the list to
                    configure its default assignees
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
